import React, { useState } from 'react';
import { Card, Button, Space, Typography, Statistic, Row, Col } from 'antd';
import { Line } from '@ant-design/charts';
import * as JapaneseHolidays from 'japanese-holidays';

const { Title } = Typography;

interface TrendData {
  date: string;
  activeUsers: number;
  paidUsers: number;
  totalDuration: number;
}

interface ChartSectionProps {
  trendData: TrendData[];
  loading: boolean;
}

const ChartSection: React.FC<ChartSectionProps> = ({ trendData, loading }) => {
  // 格式化日期
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
    const weekday = weekdays[date.getDay()];
    return `${date.getMonth() + 1}/${date.getDate()}(${weekday})`;
  };

  // 检查是否为日本祝日
  const checkJapaneseHoliday = (dateStr: string) => {
    const date = new Date(dateStr);
    return JapaneseHolidays.isHoliday(date);
  };

  // 获取祝日名称
  const getJapaneseHolidayName = (dateStr: string) => {
    const date = new Date(dateStr);
    const holidayName = JapaneseHolidays.isHoliday(date);
    return holidayName || '';
  };

  // 获取图表数据
  const getChartData = () => {
    return trendData.map(item => ({
      date: formatDate(item.date),
      fullDate: item.date,
      value: item.activeUsers,
      isHoliday: checkJapaneseHoliday(item.date),
      holidayName: getJapaneseHolidayName(item.date),
    }));
  };

  // 计算统计数据
  const getStatistics = () => {
    if (!trendData || trendData.length === 0) {
      return { average: 0, max: 0, min: 0 };
    }

    const activeUsersCounts = trendData.map(item => item.activeUsers);
    const total = activeUsersCounts.reduce((sum, count) => sum + count, 0);
    const average = Math.round(total / activeUsersCounts.length);
    const max = Math.max(...activeUsersCounts);
    const min = Math.min(...activeUsersCounts);

    return { average, max, min };
  };

  const statistics = getStatistics();

  // 图表配置
  const config = {
    data: getChartData(),
    xField: 'date',
    yField: 'value',
    smooth: true,
    height: 300,
    color: '#1890ff',
    point: {
      size: 4,
      shape: 'circle',
      style: {
        fill: 'white',
        stroke: '#1890ff',
        lineWidth: 2,
      },
    },
    area: {
      style: {
        fill: 'l(270) 0:#1890ff10 1:#1890ff20',
      },
    },
    xAxis: {
      grid: {
        line: {
          style: {
            stroke: '#f0f0f0',
          },
        },
      },
      label: {
        style: (text: string, item: any, index: number) => {
          const chartData = getChartData();
          const dataPoint = chartData[index];
          return {
            fill: dataPoint?.isHoliday ? '#ff4d4f' : '#666',
            fontWeight: dataPoint?.isHoliday ? 'bold' : 'normal'
          };
        },
      },
    },
    yAxis: {
      min: 0,
      grid: {
        line: {
          style: {
            stroke: '#f0f0f0',
          },
        },
      },
    },
    tooltip: {},
    animation: {
      appear: {
        animation: 'path-in',
        duration: 1000,
      },
    },
  };

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>アクティブユーザー数推移</Title>
          <span style={{ color: '#999', fontSize: '14px' }}>Active Users Trend</span>
        </Space>
      }
      style={{ height: '480px' }}
    >
      {/* 统计信息显示 */}
      <Row gutter={24} style={{ marginBottom: '16px' }}>
        <Col span={8} style={{ textAlign: 'center' }}>
          <Statistic
            title="平均アクティブユーザー"
            value={statistics.average}
            suffix="人"
            valueStyle={{ color: '#1890ff', fontSize: '18px' }}
          />
        </Col>
        <Col span={8} style={{ textAlign: 'center' }}>
          <Statistic
            title="最大アクティブユーザー"
            value={statistics.max}
            suffix="人"
            valueStyle={{ color: '#52c41a', fontSize: '18px' }}
          />
        </Col>
        <Col span={8} style={{ textAlign: 'center' }}>
          <Statistic
            title="最小アクティブユーザー"
            value={statistics.min}
            suffix="人"
            valueStyle={{ color: '#ff4d4f', fontSize: '18px' }}
          />
        </Col>
      </Row>
      
      {/* 图表显示 */}
      <div style={{ height: '300px' }}>
        {trendData.length > 0 ? (
          <Line {...config} />
        ) : (
          <div style={{ 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#999'
          }}>
            {loading ? '読み込み中...' : 'データがありません'}
          </div>
        )}
      </div>
    </Card>
  );
};

export default ChartSection;